import React, { useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Badge, Button, DatePicker, Dropdown, Segmented, Skeleton, Space } from "antd";
import {
  CarOutlined,
  FilePdfOutlined,
  InboxOutlined,
  LeftOutlined,
  MoreOutlined,
  PlusOutlined,
  RightOutlined,
  SettingOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { SocketContext } from "../../../context/SocketContext";
import { useAppointmentsLive } from "../../../commons/Appointments/appointmentApi";
import PageToolbar from "../../../design-system/PageToolbar";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { getBoard, getMeta, updateJob } from "./api";
import { addDays, dayOf, mondayOf, shiftRange } from "./calc";
import { saveWithAcknowledgement } from "./Conflicts";
import { DayTimeline, MobileDay, WeekGrid } from "./Board";
import JobDrawer from "./JobDrawer";
import MoveDialog from "./MoveDialog";
import { PlanModal, QueueDrawer, ResourcesDrawer, SettingsModal } from "./Panels";
import "./dispatch.css";

/** The employees of an item after moving it from one person's row to another's. */
const reassigned = (item, from, to) => {
  const people = (item.employees || []).filter(id => id !== from);
  if (to && !people.includes(to)) people.push(to);
  return to || from ? people : item.employees;
};

/**
 * Disposition (/user/dispatch): who works where, when, with which vehicle. Week view (rows =
 * employees, columns = days) and day view with hours on desktop, a day list with "move to…" on
 * phones; the unassigned queue, vehicles and skills, week plans.
 */
const Dispatch = () => {
  const { t } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const socket = useContext(SocketContext);
  const [meta, setMeta] = useState(null);
  const [board, setBoard] = useState(null);
  const [view, setView] = useState("week");
  const [day, setDay] = useState(dayOf(new Date()));
  const [drawer, setDrawer] = useState(null);
  const [moving, setMoving] = useState(null);
  const [panel, setPanel] = useState(null);
  const from = mondayOf(day);

  const loadMeta = useCallback(
    () =>
      getMeta()
        .then(res => setMeta(res.data.data))
        .catch(generalErrorAlert),
    []
  );
  const loadBoard = useCallback(
    () =>
      getBoard(from, 7)
        .then(res => setBoard(res.data.data))
        .catch(generalErrorAlert),
    [from]
  );

  useEffect(() => {
    loadMeta();
  }, []);
  // Deep links from a project or a maintenance visit: /user/dispatch?project=… | ?visit=…
  // (once the form's lists are loaded)
  const location = useLocation();
  const metaLoaded = Boolean(meta);
  useEffect(() => {
    if (!metaLoaded) return;
    const query = new URLSearchParams(location.search);
    const [type, id] = query.get("project")
      ? ["project", query.get("project")]
      : query.get("visit")
        ? ["maintenance", query.get("visit")]
        : [];
    if (type && /^[a-f0-9]{24}$/i.test(id)) {
      setDrawer({ preset: { source: { type, id }, day: query.get("day") || day } });
    }
  }, [location.search, metaLoaded]);
  useEffect(() => {
    loadBoard();
  }, [loadBoard]);
  // A change in the calendar (or by another planner) refreshes the board
  useAppointmentsLive(socket, loadBoard);

  const can = meta?.can || {};
  const step = view === "day" || isMobile ? 1 : 7;

  const move = async (item, target) => {
    const range = shiftRange(item, { day: target.day, startMinutes: target.startMinutes });
    const employees = reassigned(item, target.from, target.employee);
    const res = await saveWithAcknowledgement(acknowledge =>
      updateJob(item.id, {
        start: range.start.toISOString(),
        end: range.end.toISOString(),
        employees,
        acknowledge
      })
    );
    if (res) {
      Success(t("Saved"));
      loadBoard();
    }
  };

  const plan = (entry, target = {}) => {
    if (entry.kind === "appointment") {
      setDrawer({ id: entry.id });
      return;
    }
    setDrawer({
      preset: {
        source: { type: entry.kind, id: entry.id },
        sourceLabel: entry.title,
        title: entry.kind === "task" ? entry.title : "",
        day: target.day || entry.day || day,
        employees: target.employee ? [target.employee] : entry.employees || [],
        address: entry.address || ""
      }
    });
  };

  const drop = (payload, target) => {
    if (!payload || !can.update) return;
    if (payload.type === "item") {
      const item = board.items.find(i => i.id === payload.id);
      if (item) move(item, { ...target, from: payload.from });
      return;
    }
    if (payload.type === "queue") {
      const entry = payload.entry;
      if (entry.kind === "appointment") {
        const item = { ...entry, employees: [] };
        move(item, { ...target, from: null });
      } else {
        plan(entry, target);
      }
    }
  };

  const changed = () => {
    setDrawer(null);
    setMoving(null);
    loadBoard();
  };

  const moreItems = [
    { key: "resources", icon: <CarOutlined />, label: t("Vehicles and team") },
    { key: "plan", icon: <FilePdfOutlined />, label: t("Week plan") },
    can.settings && { key: "settings", icon: <SettingOutlined />, label: t("Settings") }
  ].filter(Boolean);

  const days = board?.days || [];
  const title = days.length
    ? `${days[0].day.split("-").reverse().join(".")} – ${days[days.length - 1].day
        .split("-")
        .reverse()
        .join(".")}`
    : "";

  return (
    <div className="dispatch-page">
      <PageToolbar
        filters={
          <Space wrap>
            <Button
              icon={<LeftOutlined className="dsp-flip" />}
              aria-label={t("Back")}
              onClick={() => setDay(addDays(day, -step))}
            />
            <Button onClick={() => setDay(dayOf(new Date()))}>{t("Today")}</Button>
            <Button
              icon={<RightOutlined className="dsp-flip" />}
              aria-label={t("Next")}
              onClick={() => setDay(addDays(day, step))}
            />
            <DatePicker
              value={dayjs(day)}
              allowClear={false}
              format={view === "day" || isMobile ? "dd DD.MM.YYYY" : `[${t("Week")}] w · DD.MM.`}
              onChange={value => value && setDay(value.format("YYYY-MM-DD"))}
            />
            {!isMobile && (
              <Segmented
                value={view}
                onChange={setView}
                options={[
                  { value: "week", label: t("Week") },
                  { value: "day", label: t("Day") }
                ]}
              />
            )}
          </Space>
        }
        actions={
          <Space wrap>
            <Badge count={board?.queue?.length || 0} size="small">
              <Button icon={<InboxOutlined />} onClick={() => setPanel("queue")}>
                {t("To be planned")}
              </Button>
            </Badge>
            <Dropdown
              menu={{ items: moreItems, onClick: ({ key }) => setPanel(key) }}
              trigger={["click"]}
            >
              <Button icon={<MoreOutlined />} aria-label={t("More")} />
            </Dropdown>
            {can.create && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setDrawer({ preset: { day } })}
              >
                {t("Plan job")}
              </Button>
            )}
          </Space>
        }
      />
      {!board || !meta ? (
        <Skeleton active />
      ) : isMobile ? (
        <MobileDay
          board={board}
          day={day}
          onDay={setDay}
          canMove={can.update}
          onOpen={item => setDrawer({ id: item.id })}
          onMove={(item, fromEmployee) => setMoving({ item, from: fromEmployee })}
        />
      ) : (
        <div className="dsp-board" aria-label={title}>
          {view === "week" ? (
            <WeekGrid
              board={board}
              canMove={can.update}
              onOpen={item => setDrawer({ id: item.id })}
              onDrop={drop}
              onAdd={can.create ? preset => setDrawer({ preset }) : null}
            />
          ) : (
            <DayTimeline
              board={board}
              day={day}
              canMove={can.update}
              onOpen={item => setDrawer({ id: item.id })}
              onDrop={drop}
            />
          )}
        </div>
      )}

      <JobDrawer
        open={Boolean(drawer)}
        meta={meta}
        id={drawer?.id}
        preset={drawer?.preset}
        onClose={() => setDrawer(null)}
        onSaved={changed}
      />
      <MoveDialog
        open={Boolean(moving)}
        item={moving?.item}
        from={moving?.from}
        staff={meta?.staff}
        onClose={() => setMoving(null)}
        onSaved={changed}
      />
      <QueueDrawer
        open={panel === "queue"}
        queue={board?.queue}
        canPlan={can.create || can.update}
        onClose={() => setPanel(null)}
        onPlan={entry => {
          setPanel(null);
          plan(entry);
        }}
      />
      <ResourcesDrawer
        open={panel === "resources"}
        meta={meta}
        onClose={() => setPanel(null)}
        onChanged={() => {
          loadMeta();
          loadBoard();
        }}
      />
      <SettingsModal
        open={panel === "settings"}
        settings={meta?.settings}
        onClose={() => setPanel(null)}
        onSaved={() => {
          setPanel(null);
          loadMeta();
          loadBoard();
        }}
      />
      <PlanModal
        open={panel === "plan"}
        from={from}
        staff={meta?.staff}
        canEmail={can.update}
        onClose={() => setPanel(null)}
      />
    </div>
  );
};

export default Dispatch;
